import logger from '../utils/logger';
import { getApiBaseUrl } from '../config/apiConfig';
/**
 * Service for fetching chatbot activity data
 */

// In-memory cache for activity data (persists during the session)
const activityCache = new Map();
let lastCacheRefreshTime = null;
const CACHE_LIFETIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches activity data for a chatbot over the last 30 days
 * @param {string} chatbotId - ID of the chatbot to fetch activity for
 * @param {boolean} forceRefresh - Whether to force a refresh ignoring cache
 * @returns {Promise<Array<number>>} - Array of message counts for the last 30 days
 */
export const fetchChatbotActivity = async (chatbotId, forceRefresh = false) => {
  try {
    if (!chatbotId) {
      logger.error('Chatbot ID is required for fetching activity data');
      return generateMockActivityData();
    }

    const now = new Date().getTime();
    const isCacheExpired = lastCacheRefreshTime && (now - lastCacheRefreshTime > CACHE_LIFETIME);

    // Check cache first if not forcing refresh and cache is not expired
    if (!forceRefresh && !isCacheExpired && activityCache.has(chatbotId)) {
      logger.log(`Using cached activity data for chatbot ${chatbotId}`);
      return activityCache.get(chatbotId);
    }

    logger.log(`Fetching fresh activity data for chatbot ${chatbotId}`);
    const response = await fetch(`${getApiBaseUrl()}/api/messages/activity/${chatbotId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch activity data: ${response.status}`);
    }

    const data = await response.json();
    logger.log(`Received activity data:`, data.activity);

    // Only use mock data if we got null or undefined
    if (!data.activity) {
      logger.warn('No activity data returned, using mock data');
      const mockData = generateMockActivityData();
      activityCache.set(chatbotId, mockData);
      return mockData;
    }

    // Store real data in cache and update cache timestamp
    activityCache.set(chatbotId, data.activity);
    lastCacheRefreshTime = now;
    return data.activity;
  } catch (error) {
    logger.error('Error fetching chatbot activity:', error);

    // If we have cached data, return it even if the refresh failed
    if (activityCache.has(chatbotId)) {
      return activityCache.get(chatbotId);
    }

    // Generate and cache mock data as fallback
    const mockData = generateMockActivityData();
    activityCache.set(chatbotId, mockData);
    return mockData;
  }
};

/**
 * Clears the activity cache for a specific chatbot or all chatbots
 * @param {string} chatbotId - Optional ID of the chatbot to clear cache for
 */
export const clearActivityCache = (chatbotId = null) => {
  if (chatbotId) {
    activityCache.delete(chatbotId);
  } else {
    activityCache.clear();
    lastCacheRefreshTime = null;
  }
};

/**
 * Generates mock activity data for testing with more dramatic peaks
 * @returns {Array<number>} - Array of mock activity counts for 30 days
 */
export const generateMockActivityData = () => {
  // Create a month-long pattern that looks natural
  const pattern = [];

  // Start with some base variability
  for (let i = 0; i < 30; i++) {
    // Lower activity on weekends
    const isWeekend = i % 7 === 0 || i % 7 === 6;
    const baseValue = isWeekend ? Math.floor(Math.random() * 3) : Math.floor(Math.random() * 5) + 2;
    pattern.push(baseValue);
  }

  // Add some realistic spikes
  const patternType = Math.floor(Math.random() * 5);

  switch (patternType) {
    case 0: // Product launch spike
      // Big spike around day 15 with gradual buildup and falloff
      for (let i = 10; i < 20; i++) {
        const distance = Math.abs(i - 15);
        const multiplier = distance === 0 ? 5 : (distance < 2 ? 3 : 2);
        pattern[i] = pattern[i] * multiplier;
      }
      break;

    case 1: // Growth trend
      // Gradual increase over the month
      for (let i = 0; i < 30; i++) {
        pattern[i] = pattern[i] + Math.floor(i * 0.5);
      }
      break;

    case 2: // Periodic spikes
      // Weekly spikes (e.g., marketing emails)
      for (let i = 0; i < 30; i++) {
        if (i % 7 === 2) { // Every Tuesday
          pattern[i] = pattern[i] * 4;
        }
      }
      break;

    case 3: // Recent spike
      // Recent activity spike
      for (let i = 20; i < 30; i++) {
        pattern[i] = pattern[i] * (i - 19);
      }
      break;

    case 4: // Volatile with multiple spikes
      // Random spikes throughout
      const spikePoints = [4, 9, 15, 21, 27];
      spikePoints.forEach(point => {
        pattern[point] = pattern[point] * 4;
        if (point > 0) pattern[point - 1] = pattern[point - 1] * 2;
        if (point < 29) pattern[point + 1] = pattern[point + 1] * 2;
      });
      break;

    default:
      // Mild increase with one spike
      const spikePoint = Math.floor(Math.random() * 20) + 5;
      pattern[spikePoint] = pattern[spikePoint] * 5;
      pattern[spikePoint + 1] = pattern[spikePoint + 1] * 3;
      break;
  }

  return pattern;
}; 